import React from "react";
import { Button } from "@/ds";
import { Icon } from "@/lib";

/** Scoped to teams: keeps rare operations keyboard-accessible without dominating each row. */
export function TeamActionMenu({ label, disabled, triggerRef, items }: {
  label: string;
  disabled: boolean;
  triggerRef?: React.MutableRefObject<HTMLButtonElement | null>;
  items: Array<{ label: string; onClick: () => void }>;
}) {
  const [open, setOpen] = React.useState(false);
  const root = React.useRef<HTMLSpanElement>(null);
  const id = React.useId();
  const first = React.useRef<"first" | "last">("first");
  const trigger = () => root.current?.querySelector<HTMLButtonElement>("button");
  const close = (restore: boolean) => {
    setOpen(false);
    if (restore) trigger()?.focus();
  };
  React.useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  React.useLayoutEffect(() => {
    if (!open) return;
    const buttons = root.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    buttons?.[first.current === "first" ? 0 : buttons.length - 1]?.focus();
  }, [open]);
  React.useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  return (
    <span className="team-action-menu" ref={root} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
    }}>
      <Button variant="ghost" iconOnly aria-label={label} id={`${id}-trigger`}
        aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} disabled={disabled}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (triggerRef) triggerRef.current = event.currentTarget;
          first.current = "first";
          setOpen(!open);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
          event.preventDefault();
          if (triggerRef) triggerRef.current = event.currentTarget;
          first.current = event.key === "ArrowUp" ? "last" : "first";
          setOpen(true);
        }}>
        <Icon name="more" size={20} />
      </Button>
      {open && <div id={id} className="menu team-action-menu__items" role="menu" aria-labelledby={`${id}-trigger`}
        onKeyDown={(event) => {
          const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
          const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
          if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(true); }
          else if (event.key === "Tab") { trigger()?.focus(); close(false); }
          else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
            const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 :
              (index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
            buttons[next]?.focus();
          }
        }}>
        {items.map(item => <button key={item.label} type="button" role="menuitem" tabIndex={-1}
          onClick={() => { close(true); item.onClick(); }}>{item.label}</button>)}
      </div>}
    </span>
  );
}
